import { stripHTML } from '@candulabs/core';
import { EventTypes } from '@candulabs/craft';
import React, { ChangeEvent, useCallback, useEffect, useRef } from 'react';
import ContentEditable, { Props as ContentEditableProps } from 'react-contenteditable';
import styled from 'styled-components';

import { useEditor } from 'craft/hooks';

const Editable = styled<any>(ContentEditable)`
  display: flex;
  user-select: text;
  flex-grow: 1;
  outline: none;

  &:hover {
    cursor: ${({ disabled }: any) => (disabled ? 'default' : 'text')};
  }
`;

interface Props extends Omit<ContentEditableProps, 'onChange'> {
  nodeId?: string;
  onEdit: (title: string) => void;
}

/**
 * Editable component that prevents newlines and deals with focus
 * TODO: move it to common craft code
 */
export const EditableTitle = ({ onEdit, nodeId, ...props }: Props) => {
  const ref = useRef<HTMLElement>();
  const { actions } = useEditor();

  const trapNewLineHandler = useCallback((e) => {
    if (e.keyCode !== 13) {
      return;
    }

    e.preventDefault();
  }, []);

  const onMouseDownHandler = useCallback(() => {
    if (!nodeId) {
      return;
    }
    actions.setEventNodes(EventTypes.selected, nodeId);
  }, [actions, nodeId]);

  useEffect(() => {
    const { current: dom } = ref;

    if (!dom) {
      return;
    }

    dom.addEventListener('keydown', trapNewLineHandler);

    // eslint-disable-next-line consistent-return
    return () => {
      dom.removeEventListener('keydown', trapNewLineHandler);
    };
  });

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onEdit(stripHTML(e.target.value)),
    [onEdit],
  );

  return (
    <Editable
      {...props}
      onMouseDown={onMouseDownHandler}
      innerRef={ref}
      onChange={onChange}
      data-craft-ignore-events
    />
  );
};
